import { AppDispatch, RootState } from "src/store";

import type { CharStyleFormattingOrigin } from "src/services/character_styles/types";

export type OutlineSortMode = "count-asc" | "count-desc" | "book_section";
export type OutlineMode = "chapter" | "character" | "style";
export enum ReviewMode {
  AllParagraphs = "all",
  ChangesOnly = "changes",
}

/** Represents a document paragraph with style and tracked-change metadata. */
export interface ParagraphInfo {
  /** Document-order index (zero-based). */
  index: number;
  /** Persistent content control tag (mss:p:<UUID>) — survives document reopen. Used to remap stale uniqueLocalId on session resume. Empty string for records created before this field was added. */
  paragraphTag: string;
  /** Session-scoped unique ID for this paragraph (stable across edits). */
  uniqueLocalId: string;
  /** Current applied style name (internal name). */
  currentStyle: string;
  /** Full descriptive name from the style profile. */
  fullStyleName: string;
  /** Category from the style profile, "Not in Profile" if unmapped. */
  category: string;
  /** Number of non-text tracked changes on this paragraph (0 = no pending changes). */
  trackedChangeCount: number;
  /** True if the backend marked this paragraph as locked (maintain-existing-styles). */
  locked: boolean;
}

export interface CharacterInfo {
  /** Document-order index (zero-based). */
  index: number;
  id: string;
  isPending: boolean;
  /** Persistent content control tag (mss:p:<UUID>) — survives document reopen. Used to remap stale uniqueLocalId on session resume. Empty string for records created before this field was added. */
  paragraphTag: string;
  /** Session-scoped unique ID for this paragraph (stable across edits). */
  paragraphUniqueLocalId: string;
  /** Current applied style name (internal name). */
  currentStyle: string;
  /** Full descriptive name from the style profile. */
  fullStyleName: string;
  /** Category from the style profile, "Not in Profile" if unmapped. */
  category: string;
  /** Number of non-text tracked changes on this paragraph (0 = no pending changes). */
  trackedChangeCount: number;
  rangeStart: number;
  rangeEnd: number;
  /** Whether the character style came from Word source formatting, MSS inference, or a PE restyle. */
  formattingOrigin: CharStyleFormattingOrigin;
}


/** Summary metadata for a style profile, matching the backend ProfileSummary model. */
export interface ProfileSummary {
  name: string;
  display_name: string;
  description: string;
  style_count: number;
  parent: string | null;
}

/** Client-side configuration returned by the backend config endpoint. */
export interface ClientConfig {
  telemetryEnabled: boolean;
  applicationInsightsConnectionString: string | null;
}

/**
 * Represents detailed information about a Word style.
 */
export interface StyleDetailInfo {
  name: string; // The internal name of the style
  fullName: string; // The full descriptive name from the style profile
  category: string; // The category from the style profile (e.g., "Body Text", "Front Matter")
  type: string; // The type of style (Paragraph, Character, etc.)
  paragraphCount: number; // Number of paragraphs using this style in the document
}

/**
 * Represents a single style definition in a style profile.
 *
 * This interface matches the `FlatStyle` dataclass returned by the
 * `GET /api/style-profiles/{name}/styles` endpoint field-for-field.
 */
export interface StyleDefinition {
  style: string;
  category: string;
  full_name: string;
  definition: string;
  style_type: "parag" | "char";
}

export type StyleDefinitionMap = Record<string, StyleDefinition>;

export interface SectionNode {
  type: string;
  identifying_style: string;
  labeling_style?: string;
}

export interface ProfileSection {
  name: string;
  ordered_categories: string[];
  nodes?: SectionNode[];
}

export interface CharacterStyleProfileEntry {
  style: string;
  full_name: string;
  source_style: string | null;
  applies_to: string[];
  removal_categories: string[];
  removal_styles: string[];
}

export interface StyleProfileResponse {
  name: string;
  description: string;
  book_section?: ProfileSection[];
  fiction?: boolean;
  style_set: Array<Record<string, unknown>>;
  character_styles?: CharacterStyleProfileEntry[];
}

export type EntityHintType = "publication" | "foreign_word" | "fraction" | "speaker" | "url" | "text_message";

export interface EntityHint {
  text: string;
  type: EntityHintType;
  start_index: number;
  confidence: number;
}

/**
 * Interface for a single line in the classification request
 */
export interface ClassificationLine {
  index: number;
  raw_text: string;
  raw_style: string;
  paragraph_id?: string;
  candidates?: Array<{
    style: string;
    source: string;
    reasoning: string;
    confidence: number;
  }>;
  entity_hints?: EntityHint[];
}

/**
 * Request payload for the classification API
 */
export interface ClassificationRequest {
  title?: string;
  bisac?: string;
  paragraphs: ClassificationLine[];
}

/**
 * A progress heartbeat emitted by the classification stream.
 *
 * Mirrors the backend ``ProgressEvent`` dataclass — the ``stage``
 * field matches ``ProgressStage`` values (e.g. ``"processing"``).
 */
export interface ClassificationProgressEvent {
  stage: "pre_processing" | "chunking" | "processing" | "refining" | "post_processing";
  completed?: number;
  total?: number;
}

/**
 * Response from the classification API
 */
export interface ClassificationResponse {
  lines: Array<{
    index: number;
    has_text: boolean;
    style: string;
    paragraph_id?: string;
    entity_hints?: EntityHint[];
    differentiations: string[] | null;
    locked?: boolean;
    candidates: Array<{
      style: string;
      source: string;
      reasoning: string | null;
      confidence: number;
    }>;
  }>;
}

/**
 * Represents a profile style that is not available in the document.
 */
export interface UnavailableProfileStyle {
  profileStyleName: string; // Style name from profile (e.g., "Heading 3")
  styleCode: string; // Style code from profile (e.g., "h3")
  styleTag: string; // Normalized tag (e.g., "h3")
}

export type ThunkApiConfig<TRejectValue> = {
  state: RootState;
  dispatch: AppDispatch;
  rejectValue: TRejectValue;
};
